import { FC, useState, useEffect, useRef } from "react";
import styles from "./CreatePresetModal.module.scss";
import { CloseIcon } from "../../../assets/svg/CloseIcon";

interface CreatePresetModalProps {
  closeFunc: () => void;
  savePreset: (customName?: string, forceLocal?: boolean) => Promise<void>;
  loadPresets: () => Promise<void>;
}

export const CreatePresetModal: FC<CreatePresetModalProps> = ({ closeFunc, savePreset, loadPresets }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        closeFunc();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [closeFunc]);

  const validateName = (value: string): string => {
    if (!value.trim()) {
      return "Preset name is required";
    }
    if (value.length > 50) {
      return "Preset name must be less than 50 characters";
    }
    return "";
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    
    if (error) {
      setError("");
    }
  };

  const handleSave = async () => {
    const validationError = validateName(name);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      // Save the preset with custom name as local preset (forceLocal=true)
      await savePreset(name.trim(), true);
      await loadPresets();
      closeFunc();
    } catch (error: any) {
      setError(error.message || "Failed to create preset");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = name.trim().length > 0 && !error && !isSubmitting;

  const getSubmitButtonText = () => {
    return isSubmitting ? 'Saving...' : 'Save';
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.head}>
        <span>Create Camera Preset</span>
        <div className={styles.close} onClick={closeFunc}>
          <CloseIcon />
        </div>
      </div>

      <div className={styles.inputSection}>
        <label htmlFor="presetName">Preset Name <span className={styles.required}>*</span></label>
        <input
          type="text"
          id="presetName"
          name="presetName"
          value={name}
          onChange={handleNameChange}
          placeholder="Enter preset name..."
          disabled={isSubmitting}
          required
          className={error ? styles.inputError : ''}
        />
        
        {error && <div className={styles.error}>{error}</div>}
      </div>

      <footer>
        <div className={styles.nav}>
          <div className={styles.cancel} onClick={closeFunc}>Cancel</div>
          <div
            className={`${styles.accept} ${isFormValid ? styles.active : ""}`}
            onClick={isFormValid ? handleSave : undefined}
          >
            {getSubmitButtonText()}
          </div>
        </div>
      </footer>
    </div>
  );
}; 